<template>
    <q-dialog v-model="showDialog">
        <q-card style="min-width: 860px; max-width: 95vw;">
            <q-card-section>
                <h5 class="q-my-none">Assign Responses to Existing Paua Client</h5>
                <div class="q-mt-md q-mb-sm">
                    <div><strong>Te Kohinga Person:</strong> {{ subject?.name || '-' }}</div>
                    <div class="text-caption text-grey-8 q-mt-xs">
                        Select the responses to move onto an existing Paua client. Use the Email and Phone
                        columns to confirm you are assigning the correct responses.
                    </div>
                </div>
                <q-banner v-if="loading" rounded class="bg-grey-2 text-grey-9">
                    <q-spinner size="sm" class="q-mr-sm" />Loading Te Kohinga Person responses...
                </q-banner>
                <q-banner v-else-if="error" rounded class="bg-red-1 text-warning">
                    {{ error }}
                </q-banner>
                <q-banner v-else-if="!rows.length" rounded class="bg-grey-2 text-grey-9">
                    No responses were found for this Te Kohinga Person.
                </q-banner>
                <q-table
                    v-else
                    flat
                    bordered
                    dense
                    row-key="id"
                    :rows="rows"
                    :columns="columns"
                    :pagination="{ rowsPerPage: 10 }"
                >
                    <template v-slot:header-cell-check>
                        <q-th auto-width>
                            <q-checkbox
                                :model-value="allSelected"
                                :indeterminate="isPartiallySelected"
                                @update:model-value="toggleSelectAll"
                            />
                        </q-th>
                    </template>

                    <template v-slot:body-cell-check="props">
                        <q-td auto-width :props="props">
                            <q-checkbox
                                :model-value="selectedIds.includes(props.row.id)"
                                @update:model-value="toggleRowSelection(props.row.id, $event)"
                            />
                        </q-td>
                    </template>
                </q-table>
            </q-card-section>

            <q-separator />

            <q-card-section>
                <ClientSelect v-model="selectedClient" />
            </q-card-section>

            <q-separator />

            <q-card-actions align="right">
                <q-btn flat rounded no-caps color="negative" label="Cancel" v-close-popup />
                <q-btn
                    flat
                    rounded
                    no-caps
                    outline
                    color="primary"
                    :label="selectedIds.length ? `Assign ${selectedIds.length} response${selectedIds.length === 1 ? '' : 's'} to Paua Client` : 'Assign'"
                    :disable="!selectedIds.length || !selectedClient"
                    :loading="assigning"
                    @click="onAssign"
                />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script>
import api from "@/services/api/api";
import ClientSelect from "@/components/ClientComponents/ClientSelectComponent.vue";

export default {
    name: "ClientAssignDialog",
    components: {
        ClientSelect
    },
    emits: ['update:show', 'assign'],
    props: {
        show: {
            type: Boolean,
            default: false
        },
        // The internal/temporary client subject whose responses are being assigned.
        subject: {
            type: [Object, null],
            default: null
        },
        responseId: {
            type: [Number, String, null],
            default: null
        }
    },
    data() {
        return {
            showDialog: this.show,
            responses: [],
            loading: false,
            error: null,
            assigning: false,
            selectedIds: [],
            selectedClient: null,
        }
    },
    computed: {
        rows() {
            return this.responses;
        },
        columns() {
            return [
                { name: 'check', label: '', field: 'check', align: 'left', sortable: false },
                { name: 'title', label: 'Form Title', field: 'title', align: 'left', sortable: true },
                { name: 'updated_at', label: 'Last Updated', field: 'updated_at', align: 'left', sortable: true },
                { name: 'status', label: 'Status', field: 'status', align: 'left', sortable: true },
                { name: 'email', label: 'Email', field: 'email', align: 'left', sortable: true },
                { name: 'phone', label: 'Phone', field: 'phone', align: 'left', sortable: true }
            ];
        },
        allSelected() {
            return this.rows.length > 0 && this.selectedIds.length === this.rows.length;
        },
        isPartiallySelected() {
            return this.selectedIds.length > 0 && this.selectedIds.length < this.rows.length;
        }
    },
    watch: {
        show(newVal) {
            this.showDialog = newVal;
            if (newVal) {
                this.selectedClient = null;
                this.getClientResponses();
            }
        },
        showDialog(newVal) {
            this.$emit('update:show', newVal);
            if (!newVal) {
                this.error = null;
                this.selectedIds = [];
            }
        },
        subject: {
            deep: true,
            handler() {
                if (this.showDialog) {
                    this.selectedClient = null;
                    this.getClientResponses();
                }
            }
        }
    },
    methods: {
        // Retrieve all dynamic-form responses for this subject across every form type.
        async getClientResponses() {
            if (!this.subject) {
                this.responses = [];
                this.selectedIds = [];
                return;
            }
            this.loading = true;
            this.error = null;
            this.responses = [];

            try {
                const formTypesRes = await api.forms.getDynamicFormTypes();
                const formTypes = formTypesRes?.data?.data || [];
                const legacyName = (this.subject.name || '').trim();

                const historyRequests = formTypes.map(async (formType) => {
                    const historyRes = await api.forms.getFormHistory(formType.id, this.subject);
                    let history = historyRes?.data?.data || [];

                    // Legacy temporary responses are not scoped server-side; match by name.
                    if (this.subject.source === 'legacy_temporary') {
                        history = history.filter(
                            (item) => (item?.temporary_client_name || '').trim() === legacyName
                        );
                    }

                    return history.map((item) => ({
                        id: Number(item.id),
                        title: formType.title,
                        updated_at: this.formatDate(item.updated_at),
                        status: this.capitalise(item.status || '-'),
                        email: item.temporary_client_email || '-',
                        phone: item.temporary_client_phone || '-'
                    }));
                });

                const allRows = (await Promise.all(historyRequests)).flat();
                this.responses = allRows.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

                if (this.responseId !== null && this.responseId !== undefined) {
                    const responseIdNumber = Number(this.responseId);
                    if (this.responses.some((row) => Number(row.id) === responseIdNumber)) {
                        this.selectedIds = [responseIdNumber];
                    }
                }
            } catch (e) {
                this.error = 'Failed to load Te Kohinga Person responses.';
            } finally {
                this.loading = false;
            }
        },
        async assignToClient(responseIds) {
            this.$emit('assign', {
                client: this.selectedClient,
                responseIds
            });
        },
        formatDate(dateStr) {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            return isNaN(date.getTime()) ? '-' : date.toLocaleString('en-nz');
        },
        capitalise(string) {
            if (typeof string !== 'string') return '';
            return string
                .replace(/_/g, ' ')
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        },
        toggleSelectAll(isSelected) {
            this.selectedIds = isSelected ? this.rows.map((row) => row.id) : [];
        },
        toggleRowSelection(rowId, isSelected) {
            if (isSelected) {
                this.selectedIds = Array.from(new Set([...this.selectedIds, rowId]));
                return;
            }
            this.selectedIds = this.selectedIds.filter((id) => id !== rowId);
        },
        async onAssign() {
            if (!this.selectedIds.length) return;
            this.assigning = true;
            try {
                await this.assignToClient(this.selectedIds);
                this.showDialog = false;
            } finally {
                this.assigning = false;
            }
        }
    }
}
</script>
